import { ChangeEvent, useContext, useEffect, useRef, useState } from 'react'
import { FontIcon, Stack, TextField } from '@fluentui/react'
import { SendFilled } from '@fluentui/react-icons'

import Send from '../../assets/Send.svg'
import SendDefault from '../../assets/SendDefault.svg'

import styles from './QuestionInput.module.css'
import { ChatMessage } from '../../api'
import { AppStateContext } from '../../state/AppProvider'
import { resizeImage } from '../../utils/resizeImage'

interface Props {
  onSend: (question: ChatMessage['content'], id?: string) => void
  disabled: boolean
  placeholder?: string
  clearOnSend?: boolean
  conversationId?: string
}

export const QuestionInput = ({ onSend, disabled, placeholder, clearOnSend, conversationId }: Props) => {
  const [question, setQuestion] = useState<string>('')
  const [base64Image, setBase64Image] = useState<string | null>(null)

  const appStateContext = useContext(AppStateContext)
  const OYD_ENABLED = appStateContext?.state.frontendSettings?.oyd_enabled || false

  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const userAgent = navigator.userAgent
    setIsMobile(/android|ipad|iphone|ipod/i.test(userAgent.toLowerCase()))
  }, [])

  const sendQuestion = () => {
    if (disabled || !question.trim()) {
      return
    }

    const questionTest: ChatMessage['content'] = base64Image
      ? [
          { type: 'text', text: question },
          { type: 'image_url', image_url: { url: base64Image } }
        ]
      : question.toString()

    if (conversationId && questionTest !== undefined) {
      onSend(questionTest, conversationId)
      setBase64Image(null)
    } else {
      onSend(questionTest)
      setBase64Image(null)
    }

    if (clearOnSend) {
      setQuestion('')
    }
  }

  const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
    if (ev.key === 'Enter' && !ev.shiftKey && !(ev.nativeEvent?.isComposing === true)) {
      ev.preventDefault()
      if (isMobile) {
        setQuestion(prev => prev + '\n')
      } else sendQuestion()
    }
  }

  const onQuestionChange = (_ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    setQuestion(newValue || '')
  }

  const sendQuestionDisabled = disabled || !question.trim()

  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  // Calculate the height of a single line of text
  const lineHeight = 24 // Assuming a line height of 24px; adjust this as needed

  // Minimum and maximum heights based on line height
  const minHeight = lineHeight
  const maxHeight = lineHeight * 5

  // Function to adjust the height of the textarea
  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current
    if (textarea) {
      textarea.style.height = 'auto' // Reset the height
      const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight)
      textarea.style.height = newHeight + 'px'
    }
  }

  // Adjust the height on value change
  useEffect(() => {
    adjustTextareaHeight()
  }, [question])

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setQuestion(event.target.value || '')
  }

  return (
    <Stack horizontal className={styles.questionInputContainer}>
      <textarea
        placeholder={placeholder}
        ref={textareaRef}
        value={question}
        onChange={handleChange}
        onKeyDown={onEnterPress}
        rows={1}
        className={styles.textAreaStyle}
        style={{ color: '#5f5f5f' }}
      />
      <div
        className={
          sendQuestionDisabled ? styles.questionInputSendButtonContainer : styles.questionInputSendButtonContainerActive
        }
        role="button"
        tabIndex={0}
        aria-label="Ask question button"
        onClick={sendQuestion}
        onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? sendQuestion() : null)}>
        {sendQuestionDisabled ? (
          <img src={SendDefault} className={styles.questionInputSendButtonDisabled} alt="Send Button" />
        ) : (
          <img src={Send} className={styles.questionInputSendButton} alt="Send Button" />
        )}
      </div>
    </Stack>
  )
}
